import React, {
  useState,
  useRef,
  useCallback,
  useEffect,
  useMemo,
} from "react";
import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import { createPortal } from "react-dom";
import {
  ClockIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

// Time formatting without timezone issues
const formatTimeWithLocale = (
  timeValue,
  locale = "en-GB",
  format = "HH:mm",
  use24Hour = true
) => {
  if (!timeValue) return "";

  // Handle string times (HH:mm or HH:mm:ss format)
  if (typeof timeValue === "string" && timeValue.includes(":")) {
    const parts = timeValue.split(":");
    if (parts.length >= 2) {
      const hours = parseInt(parts[0]);
      const minutes = parseInt(parts[1]);
      const seconds = parts[2] ? parseInt(parts[2]) : 0;

      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        const hoursStr = String(hours).padStart(2, "0");
        const minutesStr = String(minutes).padStart(2, "0");
        const secondsStr = String(seconds).padStart(2, "0");

        if (!use24Hour) {
          const hour12 = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
          const ampm = hours >= 12 ? "PM" : "AM";
          const hour12Str = String(hour12).padStart(2, "0");

          switch (format) {
            case "hh:mm A":
              return `${hour12Str}:${minutesStr} ${ampm}`;
            case "hh:mm:ss A":
              return `${hour12Str}:${minutesStr}:${secondsStr} ${ampm}`;
            default:
              return `${hour12Str}:${minutesStr} ${ampm}`;
          }
        }

        switch (format) {
          case "HH:mm":
            return `${hoursStr}:${minutesStr}`;
          case "HH:mm:ss":
            return `${hoursStr}:${minutesStr}:${secondsStr}`;
          default:
            return `${hoursStr}:${minutesStr}`;
        }
      }
    }
  }

  return "";
};

// Parse string times to display values
const parseTimeFromString = (timeValue, format = "HH:mm") => {
  if (!timeValue) return null;
  if (typeof timeValue === "string" && timeValue.includes(":")) {
    const parts = timeValue.split(":");
    if (parts.length >= 2) {
      const hours = parseInt(parts[0]);
      const minutes = parseInt(parts[1]);
      const seconds = parts[2] ? parseInt(parts[2]) : 0;

      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        return { hours, minutes, seconds };
      }
    }
  }
  return null;
};

// Enhanced: Smooth manual input parsing with better backspace support
const parseManualTimeInput = (input, format = "HH:mm") => {
  if (!input || typeof input !== "string") return null;

  const numericOnly = input.replace(/[^0-9]/g, "");

  // Handle all input lengths progressively
  if (numericOnly.length >= 1) {
    let hours = 0,
      minutes = 0,
      seconds = 0;

    if (numericOnly.length === 1) {
      // Single digit: assume it's hour (1 -> 01:00)
      hours = parseInt(numericOnly);
      if (hours >= 0 && hours <= 9) {
        return `${String(hours).padStart(2, "0")}:00`;
      }
    } else if (numericOnly.length === 2) {
      // Two digits: could be hours (12 -> 12:00) or minutes if > 23
      const num = parseInt(numericOnly);
      if (num <= 23) {
        hours = num;
        return `${String(hours).padStart(2, "0")}:00`;
      } else {
        // If > 23, treat first digit as hour, second as start of minutes
        hours = parseInt(numericOnly.substring(0, 1));
        minutes = parseInt(numericOnly.substring(1, 2)) * 10; // 25 -> 2:50
        if (hours <= 2 && minutes <= 50) {
          return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(
            2,
            "0"
          )}`;
        }
        // Fallback: treat as 0X:X0
        hours = 0;
        minutes = Math.min(num, 59);
        return `00:${String(minutes).padStart(2, "0")}`;
      }
    } else if (numericOnly.length === 3) {
      // Three digits: H:MM format (313 -> 03:13)
      hours = parseInt(numericOnly.substring(0, 1));
      minutes = parseInt(numericOnly.substring(1, 3));
    } else if (numericOnly.length === 4) {
      // Four digits: HH:MM format (0313 -> 03:13)
      hours = parseInt(numericOnly.substring(0, 2));
      minutes = parseInt(numericOnly.substring(2, 4));
    } else if (numericOnly.length === 5) {
      // Five digits: HH:MM:S format (03135 -> 03:13:50)
      hours = parseInt(numericOnly.substring(0, 2));
      minutes = parseInt(numericOnly.substring(2, 4));
      seconds = parseInt(numericOnly.substring(4, 5)) * 10;
    } else if (numericOnly.length >= 6) {
      // Six+ digits: HH:MM:SS format (031355 -> 03:13:55)
      hours = parseInt(numericOnly.substring(0, 2));
      minutes = parseInt(numericOnly.substring(2, 4));
      seconds = parseInt(numericOnly.substring(4, 6));
    }

    // Validate ranges
    if (
      hours >= 0 &&
      hours <= 23 &&
      minutes >= 0 &&
      minutes <= 59 &&
      seconds >= 0 &&
      seconds <= 59
    ) {
      const hoursStr = String(hours).padStart(2, "0");
      const minutesStr = String(minutes).padStart(2, "0");
      const secondsStr = String(seconds).padStart(2, "0");

      if (format === "HH:mm:ss" && numericOnly.length >= 5) {
        return `${hoursStr}:${minutesStr}:${secondsStr}`;
      }
      return `${hoursStr}:${minutesStr}`;
    }
  }

  return null;
};

// Enhanced: Progressive display formatting while typing (MUI-like)
const formatTimeDisplayValue = (value, isTyping, format = "HH:mm") => {
  if (!value) return "";

  if (isTyping && typeof value === "string" && !value.includes(":")) {
    const numericOnly = value.replace(/[^0-9]/g, "");

    if (numericOnly.length === 0) return "";
    if (numericOnly.length === 1) return `${numericOnly}_:__`;
    if (numericOnly.length === 2) return `${numericOnly}:__`;
    if (numericOnly.length === 3) {
      return `${numericOnly.substring(0, 1)}${numericOnly.substring(
        1,
        2
      )}:${numericOnly.substring(2)}_`;
    } else if (numericOnly.length === 4) {
      return `${numericOnly.substring(0, 2)}:${numericOnly.substring(2, 4)}`;
    } else if (numericOnly.length >= 5) {
      const hours = numericOnly.substring(0, 2);
      const minutes = numericOnly.substring(2, 4);
      const seconds = numericOnly.substring(4);
      return `${hours}:${minutes}:${seconds}`;
    }
  }

  return formatTimeWithLocale(value, "en-GB", format);
};

// Time Grid Component for hours
const HourGrid = React.memo(({ currentTime, onSelect, use24Hour }) => {
  const hours = useMemo(() => {
    if (use24Hour) {
      return Array.from({ length: 24 }, (_, i) => i);
    } else {
      return Array.from({ length: 12 }, (_, i) => (i === 0 ? 12 : i));
    }
  }, [use24Hour]);

  return (
    <div className="h-full flex flex-col">
      <div className="text-center text-[10px] font-medium text-gray-600 mb-1">
        Hours
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 gap-1 flex-1 overflow-y-auto">
        {hours.map((hour) => (
          <button
            key={hour}
            onClick={() => onSelect("hour", hour)}
            className={`
              rounded text-[9px] font-medium flex items-center justify-center transition-all duration-150
              ${
                currentTime.hours === hour
                  ? "bg-blue-600 text-white shadow-sm scale-105"
                  : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
              }
            `}
            style={{ height: "20px" }}
          >
            {String(hour).padStart(2, "0")}
          </button>
        ))}
      </div>
    </div>
  );
});

// Time Grid Component for minutes
const MinuteGrid = React.memo(({ currentTime, onSelect, minuteStep = 1 }) => {
  const minutes = useMemo(() => {
    return Array.from(
      { length: Math.floor(60 / minuteStep) },
      (_, i) => i * minuteStep
    );
  }, [minuteStep]);

  return (
    <div className="h-full flex flex-col">
      <div className="text-center text-[10px] font-medium text-gray-600 mb-1">
        Minutes
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 gap-1 flex-1 overflow-y-auto">
        {minutes.map((minute) => (
          <button
            key={minute}
            onClick={() => onSelect("minute", minute)}
            className={`
              rounded text-[9px] font-medium flex items-center justify-center transition-all duration-150
              ${
                currentTime.minutes === minute
                  ? "bg-blue-600 text-white shadow-sm scale-105"
                  : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
              }
            `}
            style={{ height: "20px" }}
          >
            {String(minute).padStart(2, "0")}
          </button>
        ))}
      </div>
    </div>
  );
});

// Time Grid Component for seconds
const SecondGrid = React.memo(({ currentTime, onSelect, secondStep = 1 }) => {
  const seconds = useMemo(() => {
    return Array.from(
      { length: Math.floor(60 / secondStep) },
      (_, i) => i * secondStep
    );
  }, [secondStep]);

  return (
    <div className="h-full flex flex-col">
      <div className="text-center text-[10px] font-medium text-gray-600 mb-1">
        Seconds
      </div>
      <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 gap-1 flex-1 overflow-y-auto">
        {seconds.map((second) => (
          <button
            key={second}
            onClick={() => onSelect("second", second)}
            className={`
              rounded text-[9px] font-medium flex items-center justify-center transition-all duration-150
              ${
                currentTime.seconds === second
                  ? "bg-blue-600 text-white shadow-sm scale-105"
                  : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
              }
            `}
            style={{ height: "20px" }}
          >
            {String(second).padStart(2, "0")}
          </button>
        ))}
      </div>
    </div>
  );
});

// AM/PM Selector
const AMPMSelector = React.memo(({ currentTime, onSelect, use24Hour }) => {
  if (use24Hour) return null;

  const currentAMPM = currentTime.hours >= 12 ? "PM" : "AM";

  return (
    <div className="h-full flex flex-col">
      <div className="text-center text-[10px] font-medium text-gray-600 mb-1">
        AM/PM
      </div>
      <div className="flex flex-col gap-1 flex-1">
        {["AM", "PM"].map((period) => (
          <button
            key={period}
            onClick={() => onSelect("ampm", period)}
            className={`
              rounded text-[9px] font-medium flex items-center justify-center transition-all duration-150
              ${
                currentAMPM === period
                  ? "bg-blue-600 text-white shadow-sm scale-105"
                  : "text-gray-700 hover:bg-blue-50 hover:text-blue-600"
              }
            `}
            style={{ height: "24px" }}
          >
            {period}
          </button>
        ))}
      </div>
    </div>
  );
});

// Custom hook to sync currentTime with value
const useTimeSync = (value, format, isTyping, setCurrentTime) => {
  useEffect(() => {
    if (!isTyping) {
      const parsedTime = parseTimeFromString(value, format);
      if (parsedTime) {
        setCurrentTime(parsedTime);
      }
    }
  }, [value, format, isTyping, setCurrentTime]);
};

// Helper function to get next view in sequence
const getNextView = (currentView, showSeconds, use24Hour) => {
  const viewSequence = use24Hour
    ? showSeconds
      ? ["hour", "minute", "second"]
      : ["hour", "minute"]
    : showSeconds
    ? ["hour", "minute", "second", "ampm"]
    : ["hour", "minute", "ampm"];

  const currentIndex = viewSequence.indexOf(currentView);
  if (currentIndex !== -1 && currentIndex < viewSequence.length - 1) {
    return viewSequence[currentIndex + 1];
  }
  return null; // No next view - time to close
};

// Helper to build default time with selected parts
const buildTimeWithDefaults = (currentTime, view, showSeconds) => {
  const now = new Date();
  const defaultTime = {
    hours: now.getHours(),
    minutes: 0,
    seconds: 0,
  };

  const result = { ...defaultTime };

  // Keep selected parts based on how far we got
  switch (view) {
    case "hour":
      result.hours = currentTime.hours;
      break;
    case "minute":
      result.hours = currentTime.hours;
      result.minutes = currentTime.minutes;
      break;
    case "second":
      result.hours = currentTime.hours;
      result.minutes = currentTime.minutes;
      result.seconds = currentTime.seconds;
      break;
    case "ampm":
      result.hours = currentTime.hours;
      result.minutes = currentTime.minutes;
      if (showSeconds) result.seconds = currentTime.seconds;
      break;
    default:
      break;
  }

  return result;
};

// FixedSizeTimePicker Component
const FixedSizeTimePicker = ({
  onChange,
  value,
  currentTime,
  setCurrentTime,
  disabled,
  format,
  use24Hour,
  minuteStep,
  secondStep,
  showSeconds,
  setIsOpen,
  setView,
  setIsFocused,
  setIsTyping,
  setTypedValue,
  inputRef,
  position,
  portalRef,
  view,
  autoAdvance = true, // New prop to control auto-advance
}) => {
  const handleTimeSelect = (type, selectedValue) => {
    if (disabled) return;

    let newTime = { ...currentTime };

    switch (type) {
      case "hour":
        if (use24Hour) {
          newTime.hours = selectedValue;
        } else {
          // Handle 12-hour format
          const currentAMPM = currentTime.hours >= 12 ? "PM" : "AM";
          if (selectedValue === 12) {
            newTime.hours = currentAMPM === "AM" ? 0 : 12;
          } else {
            newTime.hours =
              currentAMPM === "AM" ? selectedValue : selectedValue + 12;
          }
        }
        break;
      case "minute":
        newTime.minutes = selectedValue;
        break;
      case "second":
        newTime.seconds = selectedValue;
        break;
      case "ampm":
        if (selectedValue === "AM" && currentTime.hours >= 12) {
          newTime.hours = currentTime.hours - 12;
        } else if (selectedValue === "PM" && currentTime.hours < 12) {
          newTime.hours = currentTime.hours + 12;
        }
        break;
      default:
        break;
    }

    // Update the time immediately
    const timeString = showSeconds
      ? `${String(newTime.hours).padStart(2, "0")}:${String(
          newTime.minutes
        ).padStart(2, "0")}:${String(newTime.seconds).padStart(2, "0")}`
      : `${String(newTime.hours).padStart(2, "0")}:${String(
          newTime.minutes
        ).padStart(2, "0")}`;

    onChange(timeString);
    setCurrentTime(newTime);

    // Auto-advance to next view or close
    if (autoAdvance) {
      const nextView = getNextView(view, showSeconds, use24Hour);

      if (nextView) {
        // Advance to next view with a slight delay for smooth UX
        setTimeout(() => {
          setView(nextView);
        }, 300);
      } else {
        // No next view - close the picker
        setTimeout(() => {
          setIsOpen(false);
          setView("hour");
          setIsFocused(false);
          setIsTyping(false);
          setTypedValue("");
          inputRef.current?.blur();
        }, 500);
      }
    }
  };

  // Handle close with partial time completion
  const handleClose = () => {
    // Build time with selected parts and defaults for unselected parts
    const completedTime = buildTimeWithDefaults(currentTime, view, showSeconds);

    const timeString = showSeconds
      ? `${String(completedTime.hours).padStart(2, "0")}:${String(
          completedTime.minutes
        ).padStart(2, "0")}:${String(completedTime.seconds).padStart(2, "0")}`
      : `${String(completedTime.hours).padStart(2, "0")}:${String(
          completedTime.minutes
        ).padStart(2, "0")}`;

    onChange(timeString);
    setCurrentTime(completedTime);

    setIsOpen(false);
    setView("hour");
    setIsFocused(false);
    setIsTyping(false);
    setTypedValue("");
    inputRef.current?.blur();
  };

  if (!position.width) return null;

  return (
    <div
      ref={portalRef}
      className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden"
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        width: position.width,
        height: position.height,
        zIndex: 99999,
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
      }}
      role="dialog"
      aria-label="Time picker"
    >
      {!position.isConstrained && position.placement === "bottom" && (
        <div
          className="absolute"
          style={{
            top: -6,
            left: 16,
            width: 0,
            height: 0,
            borderLeft: "6px solid transparent",
            borderRight: "6px solid transparent",
            borderBottom: "6px solid white",
            filter: "drop-shadow(0 -2px 3px rgba(0,0,0,0.1))",
          }}
        />
      )}

      <div
        className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-2 py-1"
        style={{ height: 30 }}
      >
        <div className="flex items-center justify-between h-full">
          <div className="flex items-center space-x-1">
            <button
              onClick={() => setView("hour")}
              className={`text-[11px] font-medium px-2 py-1 rounded transition-all duration-150 ${
                view === "hour"
                  ? "bg-white bg-opacity-20 scale-105"
                  : "opacity-90 hover:opacity-100 hover:bg-white hover:bg-opacity-10"
              }`}
              type="button"
            >
              Hours
            </button>
            <button
              onClick={() => setView("minute")}
              className={`text-[11px] font-medium px-2 py-1 rounded transition-all duration-150 ${
                view === "minute"
                  ? "bg-white bg-opacity-20 scale-105"
                  : "opacity-90 hover:opacity-100 hover:bg-white hover:bg-opacity-10"
              }`}
              type="button"
            >
              Minutes
            </button>
            {showSeconds && (
              <button
                onClick={() => setView("second")}
                className={`text-[11px] font-medium px-2 py-1 rounded transition-all duration-150 ${
                  view === "second"
                    ? "bg-white bg-opacity-20 scale-105"
                    : "opacity-90 hover:opacity-100 hover:bg-white hover:bg-opacity-10"
                }`}
                type="button"
              >
                Seconds
              </button>
            )}
            {!use24Hour && (
              <button
                onClick={() => setView("ampm")}
                className={`text-[11px] font-medium px-2 py-1 rounded transition-all duration-150 ${
                  view === "ampm"
                    ? "bg-white bg-opacity-20 scale-105"
                    : "opacity-90 hover:opacity-100 hover:bg-white hover:bg-opacity-10"
                }`}
                type="button"
              >
                AM/PM
              </button>
            )}
          </div>

          <div className="text-center">
            <span className="text-[12px] font-semibold">
              {formatTimeWithLocale(value, "en-GB", format, use24Hour) ||
                "Select Time"}
            </span>
          </div>
        </div>
      </div>

      <div className="p-2" style={{ height: 160, overflow: "hidden" }}>
        {view === "hour" && (
          <div className="h-full">
            <HourGrid
              currentTime={currentTime}
              onSelect={handleTimeSelect}
              use24Hour={use24Hour}
            />
          </div>
        )}
        {view === "minute" && (
          <div className="h-full">
            <MinuteGrid
              currentTime={currentTime}
              onSelect={handleTimeSelect}
              minuteStep={minuteStep}
            />
          </div>
        )}
        {view === "second" && showSeconds && (
          <div className="h-full">
            <SecondGrid
              currentTime={currentTime}
              onSelect={handleTimeSelect}
              secondStep={secondStep}
            />
          </div>
        )}
        {view === "ampm" && !use24Hour && (
          <div className="h-full">
            <AMPMSelector
              currentTime={currentTime}
              onSelect={handleTimeSelect}
              use24Hour={use24Hour}
            />
          </div>
        )}
      </div>

      <div
        className="bg-gray-50 border-t border-gray-200 flex items-center justify-between px-2 py-1"
        style={{ height: 30 }}
      >
        <button
          onClick={() => {
            const now = new Date();
            const currentHours = now.getHours();
            const currentMinutes = now.getMinutes();
            const currentSeconds = now.getSeconds();

            const newTime = {
              hours: currentHours,
              minutes: currentMinutes,
              seconds: currentSeconds,
            };

            const timeString = showSeconds
              ? `${String(currentHours).padStart(2, "0")}:${String(
                  currentMinutes
                ).padStart(2, "0")}:${String(currentSeconds).padStart(2, "0")}`
              : `${String(currentHours).padStart(2, "0")}:${String(
                  currentMinutes
                ).padStart(2, "0")}`;

            onChange(timeString);
            setCurrentTime(newTime);
            setIsOpen(false);
            setView("hour");
            setIsFocused(false);
            setIsTyping(false);
            setTypedValue("");
            inputRef.current?.blur();
          }}
          className="px-2 py-1 text-blue-600 hover:bg-blue-50 rounded text-[10px] font-medium transition-all duration-150"
          type="button"
        >
          Now
        </button>

        <div className="flex items-center space-x-1">
          {onChange && value && (
            <button
              onClick={() => {
                onChange("");
                setIsOpen(false);
                setIsFocused(false);
                setIsTyping(false);
                setTypedValue("");
                inputRef.current?.blur();
              }}
              className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded text-[10px] transition-all duration-150"
              type="button"
            >
              Clear
            </button>
          )}
          <button
            onClick={handleClose}
            className="px-2 py-1 text-gray-600 hover:bg-gray-100 rounded text-[10px] transition-all duration-150"
            type="button"
          >
            Cancel
          </button>
          <button
            onClick={handleClose}
            className="px-2 py-1 bg-blue-600 text-white hover:bg-blue-700 rounded text-[10px] font-medium transition-all duration-150"
            type="button"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

// Inner component to handle Controller rendering
const TimePickerInner = ({
  name,
  onChange,
  value,
  fieldState,
  disabled,
  placeholder,
  error,
  className,
  fontSize,
  inputSize,
  inputBgColor,
  isFloatingLabel,
  leftIcon,
  rightIcon,
  iconBgColor,
  onLeftIconClick,
  onRightIconClick,
  locale,
  format,
  use24Hour,
  minuteStep,
  secondStep,
  showSeconds,
  onClear,
  allowManualInput,
  onManualInputError,
  defaultValue,
  label,
  minTime,
  maxTime,
  autoAdvance,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState({
    hours: 0,
    minutes: 0,
    seconds: 0,
  });
  const [view, setView] = useState("hour");
  const [isFocused, setIsFocused] = useState(false);
  const [position, setPosition] = useState({});
  const [isTyping, setIsTyping] = useState(false);
  const [typedValue, setTypedValue] = useState("");

  const wrapperRef = useRef(null);
  const portalRef = useRef(null);
  const inputRef = useRef(null);

  // Sync currentTime with value
  useTimeSync(value, format, isTyping, setCurrentTime);

  // Initialize with defaultValue
  useEffect(() => {
    if (defaultValue) {
      const parsedTime = parseTimeFromString(defaultValue, format);
      if (parsedTime) {
        setCurrentTime(parsedTime);
      }
    }
  }, [defaultValue, format]);

  // Reset view to hour when opening
  useEffect(() => {
    if (isOpen) {
      setView("hour");
    }
  }, [isOpen]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setIsOpen(false);
      setView("hour");
      setIsFocused(false);
      setIsTyping(false);
      setTypedValue("");
    };
  }, []);

  // Smart position calculation for time picker (same as before)
  const calculateSmartPosition = useCallback(() => {
    if (!wrapperRef.current || !isOpen) return;

    const inputRect = wrapperRef.current.getBoundingClientRect();
    const scrollX = window.pageXOffset;
    const scrollY = window.pageYOffset;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    const MODAL_WIDTH = 280;
    const MODAL_HEIGHT = 220;

    let constrainingContainer = null;
    let element = wrapperRef.current.parentElement;

    while (element && element !== document.body) {
      const computedStyle = window.getComputedStyle(element);
      const overflow = computedStyle.overflow;
      const overflowY = computedStyle.overflowY;
      const position = computedStyle.position;

      if (
        (element.hasAttribute("role") &&
          (element.getAttribute("role") === "dialog" ||
            element.getAttribute("role") === "modal")) ||
        element.classList.contains("modal") ||
        element.classList.contains("dialog") ||
        element.hasAttribute("aria-modal") ||
        position === "fixed" ||
        overflow === "hidden" ||
        overflow === "auto" ||
        overflow === "scroll" ||
        overflowY === "hidden" ||
        overflowY === "auto" ||
        overflowY === "scroll"
      ) {
        constrainingContainer = element;
        break;
      }
      element = element.parentElement;
    }

    let containerRect = {
      top: 0,
      left: 0,
      right: viewportWidth,
      bottom: viewportHeight,
      width: viewportWidth,
      height: viewportHeight,
    };
    if (constrainingContainer) {
      containerRect = constrainingContainer.getBoundingClientRect();
    }

    const spaceBelow = containerRect.bottom - inputRect.bottom;
    const spaceAbove = inputRect.top - containerRect.top;
    const spaceLeft = inputRect.left - containerRect.left;
    const spaceRight = containerRect.right - inputRect.right;

    let top,
      left,
      placement = "bottom";

    if (spaceBelow >= MODAL_HEIGHT + 8) {
      top = inputRect.bottom + scrollY + 4;
      placement = "bottom";
    } else if (spaceAbove >= MODAL_HEIGHT + 8) {
      top = inputRect.top + scrollY - MODAL_HEIGHT - 4;
      placement = "top";
    } else {
      if (spaceBelow >= spaceAbove) {
        top = Math.max(
          containerRect.top + scrollY + 8,
          inputRect.bottom + scrollY + 4
        );
        placement = "bottom";
      } else {
        top = Math.min(
          containerRect.bottom + scrollY - MODAL_HEIGHT - 8,
          inputRect.top + scrollY - MODAL_HEIGHT - 4
        );
        placement = "top";
      }
    }

    if (spaceRight >= MODAL_WIDTH) {
      left = inputRect.left + scrollX;
    } else if (spaceLeft >= MODAL_WIDTH) {
      left = inputRect.right + scrollX - MODAL_WIDTH;
    } else {
      left =
        containerRect.left + scrollX + (containerRect.width - MODAL_WIDTH) / 2;
    }

    left = Math.max(
      containerRect.left + scrollX + 8,
      Math.min(left, containerRect.right + scrollX - MODAL_WIDTH - 8)
    );

    top = Math.max(
      containerRect.top + scrollY + 8,
      Math.min(top, containerRect.bottom + scrollY - MODAL_HEIGHT - 8)
    );

    setPosition({
      top,
      left,
      placement,
      width: MODAL_WIDTH,
      height: MODAL_HEIGHT,
      isConstrained: !!constrainingContainer,
    });
  }, [isOpen]);

  // Handle outside clicks and window events (same as before)
  useEffect(() => {
    if (isOpen) {
      calculateSmartPosition();

      const handleClick = (e) => {
        if (
          wrapperRef.current &&
          !wrapperRef.current.contains(e.target) &&
          portalRef.current &&
          !portalRef.current.contains(e.target)
        ) {
          // Handle close with partial completion
          const completedTime = buildTimeWithDefaults(
            currentTime,
            view,
            showSeconds
          );

          const timeString = showSeconds
            ? `${String(completedTime.hours).padStart(2, "0")}:${String(
                completedTime.minutes
              ).padStart(2, "0")}:${String(completedTime.seconds).padStart(
                2,
                "0"
              )}`
            : `${String(completedTime.hours).padStart(2, "0")}:${String(
                completedTime.minutes
              ).padStart(2, "0")}`;

          onChange(timeString);
          setCurrentTime(completedTime);

          setIsOpen(false);
          setView("hour");
          setIsFocused(false);
          setIsTyping(false);
          setTypedValue("");
          inputRef.current?.blur();
        }
      };

      const handleResize = calculateSmartPosition;
      const handleScroll = calculateSmartPosition;

      document.addEventListener("mousedown", handleClick);
      window.addEventListener("resize", handleResize);
      document.addEventListener("scroll", handleScroll, true);

      return () => {
        document.removeEventListener("mousedown", handleClick);
        window.removeEventListener("resize", handleResize);
        document.removeEventListener("scroll", handleScroll, true);
      };
    }
  }, [
    isOpen,
    calculateSmartPosition,
    currentTime,
    view,
    showSeconds,
    onChange,
    setCurrentTime,
  ]);

  const getHeightClass = () => {
    const sizes = ["h-7", "h-8", "h-9", "h-10"];
    return sizes.includes(inputSize) ? inputSize : "h-8";
  };

  const getFontSizeClass = () => {
    const sizes = ["text-xs", "text-sm", "text-base"];
    return sizes.includes(fontSize) ? fontSize : "text-xs";
  };

  // Enhanced: Manual input handling with AM/PM support (same as before)
  const handleManualInput = (nextVal, onChange) => {
    // Handle AM/PM characters for 12-hour format
    if (!use24Hour) {
      const lowerVal = nextVal.toLowerCase();
      const hasAMPM = lowerVal.includes("a") || lowerVal.includes("p");

      if (hasAMPM) {
        // Extract numbers and AM/PM indicator
        const numericPart = nextVal.replace(/[^0-9]/g, "");
        const isAM = lowerVal.includes("a");
        const isPM = lowerVal.includes("p");

        if (numericPart && (isAM || isPM)) {
          // Parse the numeric part first
          const parsed = parseManualTimeInput(numericPart, format);

          if (parsed) {
            const timeObj = parseTimeFromString(parsed, format);
            if (timeObj) {
              let adjustedHours = timeObj.hours;

              if (isAM && adjustedHours >= 12) {
                // Convert to AM
                adjustedHours -= 12;
              } else if (isPM && adjustedHours < 12) {
                // Convert to PM
                adjustedHours += 12;
              }

              const finalTimeString = showSeconds
                ? `${String(adjustedHours).padStart(2, "0")}:${String(
                    timeObj.minutes
                  ).padStart(2, "0")}:${String(timeObj.seconds).padStart(
                    2,
                    "0"
                  )}`
                : `${String(adjustedHours).padStart(2, "0")}:${String(
                    timeObj.minutes
                  ).padStart(2, "0")}`;

              onChange(finalTimeString);
              setCurrentTime({
                hours: adjustedHours,
                minutes: timeObj.minutes,
                seconds: timeObj.seconds,
              });
              setIsTyping(false);
              setTypedValue("");
              return;
            }
          }
        }
      }
    }

    // Continue with normal numeric processing (same as before)
    const cleanedValue = nextVal.replace(/[^0-9]/g, "");
    const previousValue = value ? value.replace(/[^0-9]/g, "") : "";

    // Detect if this is a backspace operation (fewer digits than before)
    const isBackspace = cleanedValue.length < previousValue.length;

    // Set typing state for visual feedback
    setIsTyping(true);
    setTypedValue(cleanedValue);

    // If empty, clear everything
    if (!cleanedValue) {
      onChange("");
      setIsTyping(false);
      setTypedValue("");
      return;
    }

    // For backspace operations, be more immediate
    if (isBackspace) {
      // Handle progressive backspace deletion
      if (cleanedValue.length === 0) {
        onChange("");
      } else {
        // Parse the remaining digits
        const parsed = parseManualTimeInput(cleanedValue, format);
        if (parsed) {
          onChange(parsed);
          const timeObj = parseTimeFromString(parsed, format);
          if (timeObj) setCurrentTime(timeObj);
        }
      }

      // Keep typing state active for backspace briefly
      setTimeout(() => {
        if (cleanedValue.length === 0) {
          setIsTyping(false);
          setTypedValue("");
        }
      }, 50);
      return;
    }

    // For forward typing, use existing logic with delay
    const parsed = parseManualTimeInput(cleanedValue, format);

    if (parsed) {
      // Small delay for smooth forward typing
      setTimeout(() => {
        onChange(parsed);
        const timeObj = parseTimeFromString(parsed, format);
        if (timeObj) setCurrentTime(timeObj);

        // Only stop typing state if input is complete
        if (cleanedValue.length >= (showSeconds ? 6 : 4)) {
          setIsTyping(false);
          setTypedValue("");
        }
      }, 100);
    } else if (
      cleanedValue.length >= (showSeconds ? 6 : 4) &&
      onManualInputError
    ) {
      onManualInputError("Invalid time format");
      setIsTyping(false);
      setTypedValue("");
    }
  };

  // Enhanced: Keyboard navigation with AM/PM toggle support (same as before)
  const handleKeyDown = (e) => {
    if (disabled) return;

    // Handle AM/PM toggle for 12-hour format
    if (!use24Hour && allowManualInput) {
      const key = e.key.toLowerCase();

      if (key === "a" || key === "p") {
        e.preventDefault();

        const currentTimeObj =
          parseTimeFromString(value, format) || currentTime;
        let newHours = currentTimeObj.hours;

        if (key === "a") {
          // Set to AM: if PM (12-23), subtract 12
          if (newHours >= 12) {
            newHours -= 12;
          }
        } else if (key === "p") {
          // Set to PM: if AM (0-11), add 12
          if (newHours < 12) {
            newHours += 12;
          }
        }

        const newTime = {
          hours: newHours,
          minutes: currentTimeObj.minutes,
          seconds: currentTimeObj.seconds,
        };

        const timeString = showSeconds
          ? `${String(newTime.hours).padStart(2, "0")}:${String(
              newTime.minutes
            ).padStart(2, "0")}:${String(newTime.seconds).padStart(2, "0")}`
          : `${String(newTime.hours).padStart(2, "0")}:${String(
              newTime.minutes
            ).padStart(2, "0")}`;

        onChange(timeString);
        setCurrentTime(newTime);
        return;
      }
    }

    switch (e.key) {
      case "Backspace":
        // Let the normal backspace flow happen
        if (allowManualInput) {
          // Don't preventDefault - let the input handle it naturally
        }
        break;
      case "Delete":
        // Handle delete key similar to backspace for better UX
        if (allowManualInput) {
          e.preventDefault();
          const input = e.target;
          const start = input.selectionStart;
          const end = input.selectionEnd;
          const currentValue = input.value;

          if (start === end) {
            // Single cursor position - delete one character forward
            const newValue =
              currentValue.slice(0, start) + currentValue.slice(start + 1);
            handleManualInput(newValue, onChange);
          } else {
            // Selection - delete selected text
            const newValue =
              currentValue.slice(0, start) + currentValue.slice(end);
            handleManualInput(newValue, onChange);
          }
        }
        break;
      case "ArrowUp":
      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          setIsOpen(true);
          setIsFocused(true);
        }
        break;
      case "Enter":
      case " ":
        if (!allowManualInput) {
          e.preventDefault();
          setIsOpen(!isOpen);
          setIsFocused(true);
        }
        break;
      case "Escape":
        if (isOpen) {
          e.preventDefault();
          // Handle close with partial completion
          const completedTime = buildTimeWithDefaults(
            currentTime,
            view,
            showSeconds
          );

          const timeString = showSeconds
            ? `${String(completedTime.hours).padStart(2, "0")}:${String(
                completedTime.minutes
              ).padStart(2, "0")}:${String(completedTime.seconds).padStart(
                2,
                "0"
              )}`
            : `${String(completedTime.hours).padStart(2, "0")}:${String(
                completedTime.minutes
              ).padStart(2, "0")}`;

          onChange(timeString);
          setCurrentTime(completedTime);

          setIsOpen(false);
          setIsFocused(false);
          setIsTyping(false);
          setTypedValue("");
          inputRef.current?.blur();
        }
        break;
      default:
        break;
    }
  };

  const getInputDisplay = (value) =>
    isTyping && typedValue
      ? formatTimeDisplayValue(typedValue, true, format)
      : formatTimeWithLocale(value, locale, format, use24Hour);

  const hasError = Boolean(fieldState.error || error);
  const hasValue = Boolean(value);
  const shouldFloatLabel = isFloatingLabel && (isFocused || isOpen || hasValue);

  return (
    <div className={`relative w-full ${className}`}>
      {label && (
        <label
          htmlFor={name}
          className={`
            ${
              isFloatingLabel
                ? `absolute pointer-events-none z-10 transition-all duration-200 ${
                    shouldFloatLabel
                      ? "top-0 text-[10px] scale-75 origin-top-left bg-white px-0.5"
                      : "top-1/2 text-xs scale-100 origin-center"
                  }`
                : "block mb-1"
            } ${getFontSizeClass()} ${
            hasError
              ? "text-red-500"
              : shouldFloatLabel
              ? "text-blue-600"
              : "text-gray-500"
          }`}
          style={
            isFloatingLabel
              ? {
                  left: leftIcon
                    ? shouldFloatLabel
                      ? "40px"
                      : "40px"
                    : shouldFloatLabel
                    ? "10px"
                    : "10px",
                  transform: shouldFloatLabel
                    ? "translateY(-50%) scale(0.75)"
                    : "translateY(-50%) scale(1)",
                  transformOrigin: "left center",
                }
              : {}
          }
        >
          {label}
        </label>
      )}

      <div className="relative" ref={wrapperRef}>
        <div
          className={`relative flex items-stretch transition-all duration-200 ${
            hasError
              ? "focus-within:ring-2 focus-within:ring-red-500"
              : "focus-within:ring-2 focus-within:ring-blue-500"
          } rounded-md`}
        >
          {leftIcon && (
            <div
              className={`
                flex items-center justify-center w-8 ${getHeightClass()} ${iconBgColor} border border-r-0 ${
                hasError ? "border-red-500" : "border-gray-300"
              } rounded-l-md transition-all duration-200 ${
                onLeftIconClick ? "cursor-pointer hover:opacity-80" : ""
              } ${disabled ? "opacity-50" : ""} flex-shrink-0`}
              onClick={onLeftIconClick}
            >
              {React.cloneElement(leftIcon, {
                className: `w-3 h-3 flex-shrink-0 ${
                  leftIcon?.props?.className || "text-gray-500"
                }`,
              })}
            </div>
          )}

          <div
            className={`
              flex items-center flex-1 min-w-0 ${getHeightClass()} ${getFontSizeClass()} border ${inputBgColor} ${
              hasError ? "border-red-500" : "border-gray-300"
            } transition-all duration-200 ${
              disabled
                ? "opacity-50 cursor-not-allowed"
                : allowManualInput
                ? ""
                : "cursor-pointer"
            } ${leftIcon ? "rounded-l-none border-l-0" : "rounded-l-md"} ${
              rightIcon ? "rounded-r-none border-r-0" : "rounded-r-md"
            } hover:border-gray-400 relative`}
          >
            {allowManualInput ? (
              <input
                id={name}
                ref={inputRef}
                type="text"
                value={getInputDisplay(value)}
                onChange={(e) => handleManualInput(e.target.value, onChange)}
                onKeyDown={handleKeyDown}
                onFocus={() => {
                  setIsFocused(true);
                }}
                onBlur={() => {
                  setIsFocused(false);
                  // Delay to allow for smooth completion
                  setTimeout(() => {
                    setIsTyping(false);
                    setTypedValue("");
                  }, 200);
                }}
                disabled={disabled}
                placeholder={!shouldFloatLabel ? placeholder : ""}
                className={`flex-1 min-w-0 px-2 py-0.5 bg-transparent outline-none ${getFontSizeClass()} transition-all duration-200`}
                autoComplete="off"
                inputMode="numeric"
                aria-label="Type time"
                {...rest}
              />
            ) : (
              <div
                className="flex-1 min-w-0 overflow-hidden"
                onClick={() => {
                  if (!disabled) {
                    setIsOpen(!isOpen);
                    setIsFocused(true);
                  }
                }}
                onKeyDown={handleKeyDown}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-haspopup="dialog"
                aria-expanded={isOpen}
                aria-label="Open time picker"
                ref={inputRef}
              >
                <span
                  className={`block px-2 py-0.5 truncate ${
                    hasValue ? "text-gray-900" : "text-gray-400"
                  } ${getFontSizeClass()}`}
                >
                  {hasValue
                    ? formatTimeWithLocale(value, locale, format, use24Hour)
                    : !shouldFloatLabel
                    ? placeholder
                    : ""}
                </span>
              </div>
            )}

            <div className="flex items-center gap-1 pr-2 flex-shrink-0">
              {!disabled && (
                <>
                  {hasValue && onClear && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onClear();
                        setIsTyping(false);
                        setTypedValue("");
                        onChange("");
                      }}
                      className="p-0.5 hover:bg-gray-100 rounded-full transition-all duration-150"
                      type="button"
                      aria-label="Clear time"
                    >
                      <XMarkIcon className="h-3 w-3 text-gray-500" />
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsOpen(!isOpen);
                      setIsFocused(true);
                    }}
                    className="p-0.5 hover:bg-gray-100 rounded-full transition-all duration-150"
                    type="button"
                    aria-label="Open time picker"
                  >
                    <ClockIcon className="h-3 w-3 text-gray-500 flex-shrink-0" />
                  </button>
                </>
              )}
            </div>
          </div>

          {rightIcon && (
            <div
              className={`
                flex items-center justify-center w-8 ${getHeightClass()} ${iconBgColor} border border-l-0 ${
                hasError ? "border-red-500" : "border-gray-300"
              } rounded-r-md transition-all duration-200 ${
                onRightIconClick ? "cursor-pointer hover:opacity-80" : ""
              } ${disabled ? "opacity-50" : ""} flex-shrink-0`}
              onClick={onRightIconClick}
            >
              {React.cloneElement(rightIcon, {
                className: `w-3 h-3 flex-shrink-0 ${
                  rightIcon?.props?.className || "text-gray-500"
                }`,
              })}
            </div>
          )}
        </div>
      </div>

      {isOpen &&
        !disabled &&
        typeof document !== "undefined" &&
        createPortal(
          <FixedSizeTimePicker
            onChange={onChange}
            value={value}
            currentTime={currentTime}
            setCurrentTime={setCurrentTime}
            disabled={disabled}
            format={format}
            use24Hour={use24Hour}
            minuteStep={minuteStep}
            secondStep={secondStep}
            showSeconds={showSeconds}
            setIsOpen={setIsOpen}
            setView={setView}
            setIsFocused={setIsFocused}
            setIsTyping={setIsTyping}
            setTypedValue={setTypedValue}
            inputRef={inputRef}
            position={position}
            portalRef={portalRef}
            view={view}
            autoAdvance={autoAdvance}
          />,
          document.body
        )}
    </div>
  );
};

const CommonTimePicker = ({
  name,
  control,
  label,
  disabled = false,
  placeholder = "",
  error,
  className = "",
  fontSize = "text-xs",
  inputSize = "h-8",
  inputBgColor = "bg-gray-50",
  isFloatingLabel = true,
  leftIcon,
  rightIcon,
  iconBgColor = "bg-gray-100",
  onLeftIconClick,
  onRightIconClick,
  defaultValue = "",
  locale = "en-GB",
  format = "HH:mm",
  use24Hour = true,
  minuteStep = 1,
  secondStep = 1,
  showSeconds = false,
  onClear,
  allowManualInput = true,
  onManualInputError,
  minTime,
  maxTime,
  autoAdvance = true, // New prop
  ...rest
}) => {
  return (
    <Controller
      name={name}
      control={control}
      defaultValue={defaultValue || ""}
      render={({ field: { onChange, value }, fieldState }) => (
        <TimePickerInner
          name={name}
          onChange={onChange}
          value={value}
          fieldState={fieldState}
          disabled={disabled}
          placeholder={placeholder}
          error={error}
          className={className}
          fontSize={fontSize}
          inputSize={inputSize}
          inputBgColor={inputBgColor}
          isFloatingLabel={isFloatingLabel}
          leftIcon={leftIcon}
          rightIcon={rightIcon}
          iconBgColor={iconBgColor}
          onLeftIconClick={onLeftIconClick}
          onRightIconClick={onRightIconClick}
          locale={locale}
          format={format}
          use24Hour={use24Hour}
          minuteStep={minuteStep}
          secondStep={secondStep}
          showSeconds={showSeconds}
          onClear={onClear}
          allowManualInput={allowManualInput}
          onManualInputError={onManualInputError}
          defaultValue={defaultValue}
          label={label}
          minTime={minTime}
          maxTime={maxTime}
          autoAdvance={autoAdvance}
        />
      )}
    />
  );
};

CommonTimePicker.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  placeholder: PropTypes.string,
  error: PropTypes.object,
  className: PropTypes.string,
  fontSize: PropTypes.oneOf(["text-xs", "text-sm", "text-base"]),
  inputSize: PropTypes.oneOf(["h-7", "h-8", "h-9", "h-10"]),
  inputBgColor: PropTypes.string,
  isFloatingLabel: PropTypes.bool,
  leftIcon: PropTypes.element,
  rightIcon: PropTypes.element,
  iconBgColor: PropTypes.string,
  onLeftIconClick: PropTypes.func,
  onRightIconClick: PropTypes.func,
  defaultValue: PropTypes.string,
  locale: PropTypes.string,
  format: PropTypes.oneOf(["HH:mm", "HH:mm:ss", "hh:mm A", "hh:mm:ss A"]),
  use24Hour: PropTypes.bool,
  minuteStep: PropTypes.number,
  secondStep: PropTypes.number,
  showSeconds: PropTypes.bool,
  onClear: PropTypes.func,
  allowManualInput: PropTypes.bool,
  onManualInputError: PropTypes.func,
  minTime: PropTypes.string,
  maxTime: PropTypes.string,
  autoAdvance: PropTypes.bool, // New prop
};

CommonTimePicker.defaultProps = {
  disabled: false,
  placeholder: "",
  className: "",
  fontSize: "text-xs",
  inputSize: "h-8",
  inputBgColor: "bg-gray-50",
  isFloatingLabel: true,
  iconBgColor: "bg-gray-100",
  defaultValue: "",
  locale: "en-GB",
  format: "HH:mm",
  use24Hour: true,
  minuteStep: 1,
  secondStep: 1,
  showSeconds: false,
  allowManualInput: true,
  onManualInputError: null,
  autoAdvance: true, // Default to true
};

export default CommonTimePicker;
